import { useState } from "react"
import TenantUserTable, {
  type TenantUserTableProps,
} from "@/v2-backoffice-client/tenant/user/TenantUserTable"
import { useTenantUserQuery } from "@/v2-backoffice-client/tenant/user/tenant.user.query"
import { useLicenseSeatHoldersByCustomerIds } from "@/v2-backoffice-client/license/seats/license.seats.holders.hooks"

export type TenantUserProps = Omit<
  TenantUserTableProps,
  | "isLoading"
  | "error"
  | "userTenants"
  | "seatHolders"
  | "onRefresh"
  | "onDeletedFilterChange"
> & {
  tenantId: number
  /** Fired alongside the list refetch when a remove/restore changed
   *  membership — the host refreshes its own tenant row so the filter-chip
   *  counts (All / Active / Removed) move with the rows. */
  onMembershipChanged?(): void
}

function TenantUser(props: TenantUserProps) {
  const { tenantId, onMembershipChanged, ...tenantUserTableProps } = props

  const [onlyDeleted, setOnlyDeleted] = useState<boolean | undefined>(false)

  const { data, loading, error, refetch } = useTenantUserQuery({
    variables: { tenantId, onlyDeleted },
  })

  const userTenants = data?.getUserTenantsByTenantId ?? []

  // Who holds a seat, read per owning customer among the members: a seat is
  // the one piece of a member's access that is theirs alone, and it sits on
  // the licenses of the customer that owns them, not on the team's.
  const seatHolders = useLicenseSeatHoldersByCustomerIds(
    userTenants.map((userTenant) => userTenant.User?.CustomerId),
  )

  return (
    <TenantUserTable
      tenantId={tenantId}
      isLoading={loading}
      error={error}
      userTenants={userTenants}
      seatHolders={seatHolders}
      onDeletedFilterChange={setOnlyDeleted}
      onRefresh={() => {
        void refetch()
        onMembershipChanged?.()
      }}
      {...tenantUserTableProps}
    />
  )
}

export default TenantUser
